import { match } from 'ts-pattern';
import { SettlementItem, SettlementItemByBrand } from '../../types/columns';
import {
  SettlementItemsBulkUpdateResponseSettlementItemDtoStatusEnum,
  SettlementItemsFindAllResponseDto,
  SettlementItemsFindAllResponseSettlementItemsDtoPeriodTypeEnum,
  SettlementItemsFindAllResponseSettlementItemsDtoStatusEnum,
  SettlementItemsFindAllResponseSettlementItemsDtoTypeEnum,
  SettlementItemsFindAllResponseSettlementItemsPaymentDtoPaymentMethodEnum,
  SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoManagementEnum,
  SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoTypeEnum,
  SettlementItemsFindTotalResponseTotalSettlementDto,
} from '../../../../types/api/settlement-items';
import {
  BrandsSettlementStatus,
  SettlementPaymentMethod,
  SettlementPeriodType,
  BrandsSettlementPeriodType,
  SettlementItemsTotal,
  SettlementStatus,
  SettlementType,
  SettlementManagement,
  SettlementProductManagementType,
} from '../../types/client';
import {
  BrandSettlementsFindAllResponseBrandDtoSettlementPeriodTypeEnum,
  BrandSettlementsFindAllResponseBrandDtoStatusEnum,
  BrandSettlementsFindAllResponseDto,
} from '../../../../types/api/brands';

const convertSettlementPaymentMethod = (
  paymentMethod: SettlementItemsFindAllResponseSettlementItemsPaymentDtoPaymentMethodEnum,
): SettlementPaymentMethod => {
  return match<SettlementItemsFindAllResponseSettlementItemsPaymentDtoPaymentMethodEnum, SettlementPaymentMethod>(
    paymentMethod,
  )
    .with('card', () => '신용카드')
    .with('kakaopay', () => '카카오페이')
    .with('naverpay', () => '네이버페이')
    .with('payco', () => '페이코')
    .with('phone', () => '핸드폰 결제')
    .with('point', () => '포인트 결제')
    .with('tosspay', () => '토스페이')
    .with('trans', () => '실시간 계좌이체')
    .with('vBank', () => '가상계좌')
    .otherwise(() => {
      throw new Error(`지원하지 않는 결제수단: ${paymentMethod}`);
    });
};

const convertSettlementPeriodType = (
  periodType: SettlementItemsFindAllResponseSettlementItemsDtoPeriodTypeEnum,
): SettlementPeriodType => {
  return match<SettlementItemsFindAllResponseSettlementItemsDtoPeriodTypeEnum, SettlementPeriodType>(periodType)
    .with('daily', () => '일간 정산')
    .with('monthly', () => '월간 정산')
    .otherwise(() => {
      throw new Error(`지원하지 않는 정산 기간: ${periodType}`);
    });
};

const convertBrandsSettlementPeriodType = (
  periodType: BrandSettlementsFindAllResponseBrandDtoSettlementPeriodTypeEnum,
): BrandsSettlementPeriodType => {
  return match<BrandSettlementsFindAllResponseBrandDtoSettlementPeriodTypeEnum, BrandsSettlementPeriodType>(periodType)
    .with('daily', () => '일간 정산')
    .with('monthly', () => '월간 정산')
    .otherwise(() => {
      throw new Error(`지원하지 않는 정산 기간: ${periodType}`);
    });
};

export const convertSettlementStatus = (
  status: SettlementItemsFindAllResponseSettlementItemsDtoStatusEnum,
): SettlementStatus => {
  return match<SettlementItemsBulkUpdateResponseSettlementItemDtoStatusEnum, SettlementStatus>(status)
    .with('ready', () => '대기')
    .with('completed', () => '완료')
    .with('hold', () => '보류')
    .otherwise(() => {
      throw new Error(`지원하지 않는 상태: ${status}`);
    });
};

const convertSettlementType = (type: SettlementItemsFindAllResponseSettlementItemsDtoTypeEnum): SettlementType => {
  return match<SettlementItemsFindAllResponseSettlementItemsDtoTypeEnum, SettlementType>(type)
    .with('order', () => '상품 주문')
    .with('extraDelivery', () => '추가 배송비')
    .with('returnDelivery', () => '반품 배송비')
    .otherwise(() => {
      throw new Error(`지원하지 않는 구분: ${type}`);
    });
};

const convertManagement = (
  management: SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoManagementEnum,
): SettlementManagement => {
  return match<
    SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoManagementEnum,
    SettlementManagement
  >(management)
    .with('brand', () => '브랜드')
    .with('amondz', () => '아몬즈')
    .otherwise(() => {
      throw new Error(`지원하지 않는 관리처: ${management}`);
    });
};

const convertProductManagementType = (
  productManagementType: SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoTypeEnum,
): SettlementProductManagementType => {
  return match<
    SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoTypeEnum,
    SettlementProductManagementType
  >(productManagementType)
    .with('enter', () => '입점상품')
    .with('income', () => '입고상품')
    .with('consignmentManufacture', () => '위탁생산상품')
    .otherwise(() => {
      throw new Error(`지원하지 않는 타입: ${productManagementType}`);
    });
};

export const convertSettlementItemsToColumns = (
  settlementItems: SettlementItemsFindAllResponseDto['settlementItems'],
): SettlementItem[] => {
  return settlementItems.map((item) => ({
    id: item.id,
    brandName: item.brand.name,
    brandId: item.brand.id,
    orderId: item.order.id,
    orderItemId: item.orderItem?.id,
    periodType: convertSettlementPeriodType(item.periodType),
    monthlyExecutionPeriod: item.monthlyExecutionPeriod,
    monthlyExecutionDay: item.monthlyExecutionDay,
    dueDate: item.dueDate,
    baseDate: item.baseDate,
    completeDate: item.completeDate,
    status: convertSettlementStatus(item.status),
    type: convertSettlementType(item.type),
    productId: item.orderItem?.product.id,
    pgProviderKorName: item.payment.pgProvider.korName,
    paymentMethod: convertSettlementPaymentMethod(item.payment.paymentMethod),
    userId: item.user.id,
    userName: item.user.name,
    settlementAmount: item.settlementAmount,
    salesAmount: item.salesAmount,
    usedCouponAmount: item.type === 'returnDelivery' ? undefined : item.orderItem?.usedCouponAmount,
    amondzCouponAmount:
      item.type === 'returnDelivery' ? 0 : (item.orderItem?.usedCouponAmount ?? 0) - item.couponShareAmount,
    couponShareAmount: item.type === 'returnDelivery' ? 0 : item.couponShareAmount,
    usedPointAmount: item.type === 'returnDelivery' ? undefined : item.orderItem?.usedPointAmount,
    paymentAmount: item.paidAmount,
    paymentDate: item.payment.paymentDate,
    purchaseConfirmDate: item.orderItem?.purchaseConfirmDate,
    sumFee: item.sumFee,
    management: item.orderItem?.product.productManagementType.management
      ? convertManagement(item.orderItem?.product.productManagementType.management)
      : undefined,
    productManagementType: item.orderItem?.product.productManagementType.type
      ? convertProductManagementType(item.orderItem?.product.productManagementType.type)
      : undefined,
    pgFee: item.pgFee,
    amondzFee: item.amondzFee,
    marketingFee: item.marketingFee,
    fulfillmentFee: item.fulfillmentFee,
    pgFeeVat: item.pgFeeVat,
    amondzFeeVat: item.amondzFeeVat,
    marketingFeeVat: item.marketingFeeVat,
    fulfillmentFeeVat: item.fulfillmentFeeVat,
    accountHolder: item.accountHolder,
    accountNumber: item.accountNumber,
    bankName: item.bankName,
    brandPhone: item.brand.phone,
    memo: item.memo ? item.memo : undefined,
    adminId: item.admin?.id,
    adminName: item.admin?.name,
  }));
};

export const convertSettlementItemsTotal = (
  data: SettlementItemsFindTotalResponseTotalSettlementDto,
): SettlementItemsTotal => {
  return {
    totalUserPaymentAmount: data.totalUserPaymentAmount,
    totalSalesCount: data.totalSalesCount,
    totalSettlementAmount: data.totalSettlementAmount,
    totalUsedCouponAmount: data.totalUsedCouponAmount,
    totalUsedPointAmount: data.totalUsedPointAmount,
    totalFee: data.totalFee,
    totalPgFee: data.totalPgFee,
    totalAmondzFee: data.totalAmondzFee,
    totalMarketingFee: data.totalMarketingFee,
    totalFulfillmentFee: data.totalFulfillmentFee,
    totalCount: data.totalCount,
    totalSalesAmount: data.totalSalesAmount,
    totalCouponShareAmount: data.totalCouponShareAmount,
  };
};

const convertBrandsStatus = (status: BrandSettlementsFindAllResponseBrandDtoStatusEnum): BrandsSettlementStatus => {
  return match<BrandSettlementsFindAllResponseBrandDtoStatusEnum, BrandsSettlementStatus>(status)
    .with('active', () => '활성화')
    .with('inactive', () => '비활성화')
    .with('reject', () => '입점거절')
    .with('requestJoin', () => '가입요청')
    .otherwise(() => {
      throw new Error(`지원하지 않는 브랜드 상태: ${status}`);
    });
};

export const convertBrandsToSettlementItemByBrands = (
  brands: BrandSettlementsFindAllResponseDto['brands'],
): SettlementItemByBrand[] => {
  return brands.map((brand) => {
    const consignmentManufactureFee = brand.settlements.find(
      ({ productManagementType }) => productManagementType === 'consignmentManufacture',
    );

    const enterManufactureFee = brand.settlements.find(
      ({ productManagementType }) => productManagementType === 'enter',
    );

    const incomeManufactureFee = brand.settlements.find(
      ({ productManagementType }) => productManagementType === 'income',
    );

    return {
      id: String(brand.id),
      mainImageUrl: brand.mainImageUrl,
      brandId: String(brand.id),
      brandName: brand.name,
      status: convertBrandsStatus(brand.status),
      confirmDate: brand.confirmDate,
      accountHolder: brand.accountHolder,
      accountNumber: brand.accountNumber,
      bankName: brand.bank.name,
      phone: brand.phone,
      periodType: brand.settlementPeriodType
        ? convertBrandsSettlementPeriodType(brand.settlementPeriodType)
        : undefined,

      enterIsPgFeeUsed: enterManufactureFee?.isPgFeeUsed,
      enterAmondzFeeRate: enterManufactureFee?.amondzFeeRate,
      enterMarketingFeeRate: enterManufactureFee?.marketingFeeRate,
      enterFulfillmentFeeRate: enterManufactureFee?.fulfillmentFeeRate,

      incomeIsPgFeeUsed: incomeManufactureFee?.isPgFeeUsed,
      incomeAmondzFeeRate: incomeManufactureFee?.amondzFeeRate,
      incomeMarketingFeeRate: incomeManufactureFee?.marketingFeeRate,
      incomeFulfillmentFeeRate: incomeManufactureFee?.fulfillmentFeeRate,

      consignmentIsPgFeeUsed: consignmentManufactureFee?.isPgFeeUsed,
      consignmentAmondzFeeRate: consignmentManufactureFee?.amondzFeeRate,
      consignmentMarketingFeeRate: consignmentManufactureFee?.marketingFeeRate,
      consignmentFulfillmentFeeRate: consignmentManufactureFee?.fulfillmentFeeRate,
    };
  });
};
